import { Box, Typography } from '@mui/material';
import React from 'react';

import {
  ChromeTheme,
  DeviceType,
  Orientation,
  SiteContextType,
} from '../../types/previewStudio';
import SiteContext from './SiteContextTemplates';

const SITE_CONTEXT_URLS: Record<SiteContextType, string> = {
  blank: 'example.com',
  forbes: 'forbes.com',
  people: 'people.com',
  espn: 'espn.com',
  bleacherreport: 'bleacherreport.com',
  allrecipes: 'allrecipes.com',
  myrecipes: 'myrecipes.com',
  instagram: 'instagram.com',
  facebook: 'facebook.com',
  wordswithfriends: 'wordswithfriends.com',
  subwaysurfers: 'subwaysurfers.com',
};

// Game contexts should hide browser chrome — they're in-app, not web
export const GAME_CONTEXTS: SiteContextType[] = ['wordswithfriends', 'subwaysurfers'];

interface DeviceFrameProps {
  deviceType: DeviceType;
  adWidth: number;
  adHeight: number;
  orientation?: Orientation;
  chromeTheme?: ChromeTheme;
  scale?: number;
  autoScale?: boolean;
  showSizeLabel?: boolean;
  showNameLabel?: string;
  siteContext?: SiteContextType;
  expandableMode?: boolean;
  isExpanded?: boolean;
  children: React.ReactNode;
}

function getDeviceFrameDimensions(
  deviceType: DeviceType,
  adWidth: number,
  adHeight: number,
  orientation: Orientation = 'portrait',
) {
  let dims: { width: number; height: number };
  switch (deviceType) {
    case 'mobile':
      dims = { width: 375, height: 667 };
      break;
    case 'tablet':
      dims = { width: 820, height: 1180 };
      break;
    case 'desktop':
      dims = { width: 1440, height: 900 };
      break;
    case 'ctv':
      dims = { width: 1920, height: 1080 };
      break;
    default:
      dims = { width: 375, height: 667 };
  }

  if (orientation === 'landscape' && (deviceType === 'mobile' || deviceType === 'tablet')) {
    return { width: dims.height, height: dims.width };
  }
  return dims;
}

function getDeviceFrameStyles(
  deviceType: DeviceType,
  chromeTheme: ChromeTheme,
  expandableMode: boolean,
  isExpanded: boolean,
) {
  const bezelColor = chromeTheme === 'dark' ? '#1a1a1a' : '#f0f0f0';
  const borderColor = chromeTheme === 'dark' ? '#2a2a2a' : '#d0d0d0';

  const baseStyles = {
    position: 'relative' as const,
    bgcolor: bezelColor,
    display: 'flex',
    flexDirection: 'column' as const,
    overflow: (expandableMode && isExpanded) ? 'visible' : 'hidden',
    boxSizing: 'border-box' as const,
    boxShadow: chromeTheme === 'dark'
      ? '0 8px 32px rgba(0,0,0,0.3)'
      : '0 8px 32px rgba(0,0,0,0.12)',
  };

  switch (deviceType) {
    case 'mobile':
      return { ...baseStyles, borderRadius: '36px', padding: '12px', border: `8px solid ${borderColor}` };
    case 'tablet':
      return { ...baseStyles, borderRadius: '24px', padding: '16px', border: `12px solid ${borderColor}` };
    case 'desktop':
      return { ...baseStyles, borderRadius: '8px', padding: '8px 8px 32px 8px', border: `4px solid ${borderColor}` };
    case 'ctv':
      return { ...baseStyles, borderRadius: '4px', padding: '4px', border: `2px solid ${borderColor}` };
    default:
      return baseStyles;
  }
}

export default function DeviceFrame({
  deviceType,
  adWidth,
  adHeight,
  orientation = 'portrait',
  chromeTheme = 'dark',
  scale: scaleProp,
  autoScale = true,
  showSizeLabel = false,
  showNameLabel,
  siteContext = 'blank',
  expandableMode = false,
  isExpanded = false,
  children,
}: DeviceFrameProps) {
  const [containerRef, setContainerRef] = React.useState<HTMLDivElement | null>(null);
  const [computedScale, setComputedScale] = React.useState(1);

  const deviceDims = React.useMemo(
    () => getDeviceFrameDimensions(deviceType, adWidth, adHeight, orientation),
    [deviceType, adWidth, adHeight, orientation],
  );

  const frameStyles = React.useMemo(
    () => getDeviceFrameStyles(deviceType, chromeTheme, expandableMode, isExpanded),
    [deviceType, chromeTheme, expandableMode, isExpanded],
  );

  // Auto-scale to fit container
  React.useEffect(() => {
    if (!autoScale || scaleProp !== undefined || !containerRef) return undefined;

    function calculate() {
      const rect = containerRef!.getBoundingClientRect();
      const availW = Math.max(300, rect.width - 40);
      const availH = Math.max(300, rect.height - 40);
      const sx = availW / deviceDims.width;
      const sy = availH / deviceDims.height;
      let s = Math.min(sx, sy);
      if (deviceType === 'tablet') {
        s = Math.max(0.3, Math.min(0.7, s));
      } else if (deviceType === 'mobile') {
        s = Math.max(0.4, Math.min(1.0, s));
      } else {
        s = Math.max(0.3, Math.min(1.0, s));
      }
      setComputedScale(s);
    }

    calculate();
    const observer = new ResizeObserver(calculate);
    observer.observe(containerRef);
    return () => observer.disconnect();
  }, [autoScale, scaleProp, containerRef, deviceDims, deviceType]);

  const activeScale = scaleProp ?? computedScale;

  const textColor = chromeTheme === 'dark' ? 'white' : '#333';
  const chromeBarBg = chromeTheme === 'dark' ? '#1a1a1a' : '#fafafa';
  const chromeBorder = chromeTheme === 'dark' ? '#333' : '#e0e0e0';
  const viewportBg = chromeTheme === 'dark' ? '#f5f5f5' : '#ffffff';
  const placeholderColor = chromeTheme === 'dark' ? '#e0e0e0' : '#d5d5d5';

  // When using a site context, the ad slot is just the children with minimal wrapping.
  // When using blank context, we center and pad the ad slot like the original PreviewPanel.
  const adSlot = (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        ...(siteContext === 'blank' ? { py: 2, px: 1.5 } : { py: 0, px: 0 }),
        flexShrink: 0,
        bgcolor: (() => {
          if (siteContext !== 'blank') return 'transparent';
          return (deviceType === 'mobile' || deviceType === 'tablet') ? 'white' : viewportBg;
        })(),
        overflow: (expandableMode && isExpanded) ? 'visible' : 'hidden',
        position: 'relative',
      }}
    >
      {children}
    </Box>
  );

  const frameContent = (
      <Box
        sx={{
          transform: activeScale !== 1 ? `scale(${activeScale})` : undefined,
          transformOrigin: 'top center',
          ...frameStyles,
          width: `${deviceDims.width}px`,
          height: `${deviceDims.height}px`,
        }}
      >
        {/* Mobile Status Bar */}
        {deviceType === 'mobile' && (
          <Box
            sx={{
              height: 24,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              px: 2,
              bgcolor: chromeBarBg,
              color: textColor,
              fontSize: '0.7rem',
              borderRadius: '28px 28px 0 0',
              flexShrink: 0,
            }}
          >
            <span>12:00</span>
            <Box sx={{ display: 'flex', gap: 0.5, fontSize: '0.6rem' }}>
              <span>&#9679;&#9679;&#9679;</span>
            </Box>
          </Box>
        )}

        {/* Device Viewport */}
        <Box
          sx={{
            bgcolor: viewportBg,
            borderRadius: deviceType === 'mobile' ? '0 0 28px 28px' : 1,
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'auto',
            position: 'relative',
            scrollbarWidth: 'none',
            msOverflowStyle: 'none',
            '&::-webkit-scrollbar': { display: 'none' },
          }}
        >
          {/* Address Bar (mobile/tablet, not for game contexts) */}
          {(deviceType === 'mobile' || deviceType === 'tablet') && !GAME_CONTEXTS.includes(siteContext) && (
            <>
              <Box
                sx={{
                  bgcolor: chromeTheme === 'dark' ? 'white' : '#fafafa',
                  borderBottom: `1px solid ${chromeBorder}`,
                  px: 1.5,
                  py: 1,
                  flexShrink: 0,
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box
                    sx={{
                      flex: 1,
                      bgcolor: '#f1f1f1',
                      borderRadius: 999,
                      height: 32,
                      display: 'flex',
                      alignItems: 'center',
                      px: 1.5,
                      fontSize: '0.75rem',
                      color: '#666',
                    }}
                  >
                    {SITE_CONTEXT_URLS[siteContext] || 'example.com'}
                  </Box>
                  <Box sx={{ width: 28, height: 28, borderRadius: '50%', bgcolor: '#e9e9e9' }} />
                </Box>
              </Box>
              {/* Generic header — only shown for blank context since site templates have their own */}
              {siteContext === 'blank' && (
              <Box
                sx={{
                  bgcolor: chromeTheme === 'dark' ? 'white' : '#fafafa',
                  p: 1.5,
                  borderBottom: `1px solid ${chromeBorder}`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  flexShrink: 0,
                }}
              >
                <Box sx={{ width: 32, height: 32, borderRadius: '50%', bgcolor: placeholderColor }} />
                <Box sx={{ width: 24, height: 3, bgcolor: '#999', borderRadius: 1 }} />
              </Box>
              )}
            </>
          )}

          {/* Site Context wraps the ad slot */}
          {siteContext === 'blank' ? (
            adSlot
          ) : (
            <SiteContext
              type={siteContext}
              adSlot={adSlot}
              deviceType={deviceType}
              chromeTheme={chromeTheme}
            />
          )}

          {/* Mock Content Below Ad (mobile/tablet, blank context only) */}
          {(deviceType === 'mobile' || deviceType === 'tablet') && siteContext === 'blank' && (
            <Box sx={{ flex: 1, px: 2, pb: 2, overflow: 'hidden' }}>
              <Box sx={{ bgcolor: placeholderColor, borderRadius: 1, height: 120, mb: 2 }} />
              <Box sx={{ mb: 2 }}>
                <Box sx={{ bgcolor: placeholderColor, height: 8, borderRadius: 1, mb: 1, width: '100%' }} />
                <Box sx={{ bgcolor: placeholderColor, height: 8, borderRadius: 1, mb: 1, width: '85%' }} />
                <Box sx={{ bgcolor: placeholderColor, height: 8, borderRadius: 1, width: '90%' }} />
              </Box>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Box sx={{ flex: 1 }}>
                  <Box sx={{ bgcolor: placeholderColor, borderRadius: 1, height: 80, mb: 1 }} />
                  <Box sx={{ bgcolor: placeholderColor, height: 6, borderRadius: 1, mb: 0.5, width: '100%' }} />
                  <Box sx={{ bgcolor: placeholderColor, height: 6, borderRadius: 1, width: '80%' }} />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Box sx={{ bgcolor: placeholderColor, borderRadius: 1, height: 80, mb: 1 }} />
                  <Box sx={{ bgcolor: placeholderColor, height: 6, borderRadius: 1, mb: 0.5, width: '100%' }} />
                  <Box sx={{ bgcolor: placeholderColor, height: 6, borderRadius: 1, width: '75%' }} />
                </Box>
              </Box>
            </Box>
          )}
        </Box>

        {/* Desktop Taskbar */}
        {deviceType === 'desktop' && (
          <Box
            sx={{
              height: 24,
              bgcolor: chromeTheme === 'dark' ? '#2a2a2a' : '#e0e0e0',
              display: 'flex',
              alignItems: 'center',
              px: 2,
              gap: 1,
              flexShrink: 0,
            }}
          >
            <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#666' }} />
            <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#666' }} />
            <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#666' }} />
          </Box>
        )}

        {/* Size Label Overlay */}
        {showSizeLabel && (
          <Box
            sx={{
              position: 'absolute',
              bottom: deviceType === 'desktop' ? 40 : 16,
              right: 16,
              bgcolor: 'rgba(0,0,0,0.7)',
              color: 'white',
              px: 1,
              py: 0.25,
              borderRadius: 1,
              fontSize: '0.65rem',
              fontFamily: 'monospace',
              zIndex: 5,
            }}
          >
            {adWidth}x{adHeight}
          </Box>
        )}

        {/* Name Label Overlay */}
        {showNameLabel && (
          <Box
            sx={{
              position: 'absolute',
              top: deviceType === 'mobile' ? 44 : 16,
              left: 16,
              right: 16,
              zIndex: 5,
            }}
          >
            <Typography
              variant="caption"
              sx={{
                bgcolor: 'rgba(0,0,0,0.7)',
                color: 'white',
                px: 1,
                py: 0.25,
                borderRadius: 1,
                fontSize: '0.65rem',
                display: 'inline-block',
                maxWidth: '100%',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {showNameLabel}
            </Typography>
          </Box>
        )}
      </Box>
  );

  // When autoScale is active (Preview Studio), wrap in a sizing container.
  // Otherwise (gallery cards), return the frame directly — let the parent handle layout.
  if (autoScale && scaleProp === undefined) {
    return (
      <Box
        ref={setContainerRef}
        sx={{
          flex: 1,
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'center',
          position: 'relative',
          overflow: 'hidden',
          pt: 1,
        }}
      >
        {frameContent}
      </Box>
    );
  }

  return frameContent;
}

export { getDeviceFrameDimensions };
